import { computed, ref, watch } from 'vue'
import type { LocationQueryRaw, RouteLocationNormalizedLoaded } from 'vue-router'
import { useRoute, useRouter } from 'vue-router'
import { apiFetch } from '../../services/api'

/**
 * The Actions list query: URL <-> filters, the fetch, and the ordering.
 *
 * Both the list and the detail view use this. The detail view needs the ordered
 * page to answer "what does j go to next?" — it only knows one reachout on its
 * own. It can rebuild that page because the filters live in the query params of
 * *both* routes (Gmail-style: the detail URL is the list URL plus an id
 * segment), so a pasted detail link supports j/k exactly like a clicked one.
 *
 * Sharing the parser and the fetch is what keeps the two views from disagreeing
 * about sort order — if they did, j in the detail view would silently walk a
 * different sequence than the list displayed.
 */

export const ACTIONS_PAGE_SIZE = 50

/**
 * Ceiling on "select all matching". Bulk approve/dismiss posts every id in one
 * request body, so this bounds both the fan-out fetch and that payload.
 */
export const SELECT_ALL_MAX = 2000

export type ActionsFilters = {
  type: string
  stage: string
  play: string
  owner: string
  account: string
  /** 1-based sequence step, as displayed ("Step 2 of 5"). Empty = any. */
  step: string
  q: string
  sort: string
  page: number
}

export const DEFAULT_FILTERS: ActionsFilters = {
  type: '',
  stage: 'todo',
  play: '',
  owner: '',
  account: '',
  step: '',
  q: '',
  sort: 'newest_first',
  page: 1,
}

export const STAGE_OPTIONS = [
  { value: 'todo', label: 'To Do' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'failed', label: 'Failed' },
  { value: 'deleted', label: 'Deleted' },
  { value: 'all', label: 'All' },
]

const VALID_STAGES = new Set(STAGE_OPTIONS.map((o) => o.value))

/**
 * Anything unrecognized falls back to the default rather than being passed
 * through — the server ignores an unknown stage, which would silently render an
 * unfiltered list while the dropdown claimed otherwise.
 */
function normalizeStage(raw: string): string {
  return VALID_STAGES.has(raw) ? raw : DEFAULT_FILTERS.stage
}

export function parseFilters(route: RouteLocationNormalizedLoaded): ActionsFilters {
  const str = (key: string, fallback = '') => {
    const raw = route.query[key]
    const value = Array.isArray(raw) ? raw[0] : raw
    return typeof value === 'string' && value.trim() ? value.trim() : fallback
  }
  const page = Number.parseInt(str('p', '1'), 10)
  return {
    type: str('type'),
    stage: normalizeStage(str('stage', DEFAULT_FILTERS.stage)),
    play: str('play'),
    owner: str('owner'),
    account: str('account'),
    step: str('step'),
    q: str('q'),
    sort: str('sort') === 'oldest' ? 'oldest_first' : DEFAULT_FILTERS.sort,
    page: Number.isFinite(page) && page > 0 ? page : 1,
  }
}

/**
 * Filters -> query params, omitting anything at its default so the canonical
 * URL stays clean (`/actions` rather than `/actions?p=1&sort=newest_first`).
 */
export function filtersToQuery(filters: ActionsFilters): LocationQueryRaw {
  const query: LocationQueryRaw = {}
  if (filters.type) query.type = filters.type
  if (filters.stage && filters.stage !== DEFAULT_FILTERS.stage) query.stage = filters.stage
  if (filters.play) query.play = filters.play
  if (filters.owner) query.owner = filters.owner
  if (filters.account) query.account = filters.account
  if (filters.step) query.step = filters.step
  if (filters.q) query.q = filters.q
  if (filters.sort === 'oldest_first') query.sort = 'oldest'
  if (filters.page > 1) query.p = String(filters.page)
  return query
}

function buildParams(filters: ActionsFilters) {
  const params = new URLSearchParams()
  if (filters.account) params.set('account_id', filters.account)
  if (filters.play) params.set('sales_play_id', filters.play)
  if (filters.owner) params.set('owner_user_id', filters.owner)
  if (filters.step) params.set('sequence_step', filters.step)
  if (filters.type) params.set('types', filters.type)
  if (filters.q) params.set('search', filters.q)

  // The server expands the bucket. Sending the name rather than a status list is
  // what keeps `completed` an exclusion instead of a list that goes stale.
  params.set('stage', filters.stage)
  return params
}

/**
 * ── Shared cache ──────────────────────────────────────────────────
 * Module scope, so the list and the detail page read the SAME rows rather than
 * each fetching its own copy. 
 *
 * `cursorId` lives here too: it's what makes the row selection survive
 * list → detail → back, and it advances as you j/k through detail views, so Esc
 * lands you where you actually ended up.
 */
const cachedItems = ref<any[]>([])
const cachedTotal = ref(0)
const cachedCounts = ref<Record<string, number>>({})

let cacheKey = ''
let cacheAt = 0
const cursorId = ref('')
const CACHE_MAX_AGE_MS = 2 * 60 * 1000

export function useActionsQuery() {
  const route = useRoute()
  const router = useRouter()

  const items = cachedItems
  const total = cachedTotal
  const counts = cachedCounts
  const loading = ref(false)
  const error = ref('')

  const filters = computed(() => parseFilters(route))
  const pageCount = computed(() => Math.max(1, Math.ceil(total.value / ACTIONS_PAGE_SIZE)))

  const rangeStart = computed(() => (total.value === 0 ? 0 : (filters.value.page - 1) * ACTIONS_PAGE_SIZE + 1))
  const rangeEnd = computed(() => Math.min(filters.value.page * ACTIONS_PAGE_SIZE, total.value))

  function cacheKeyFor(): string {
    return JSON.stringify(filters.value)
  }

  function cacheIsUsable(): boolean {
    return cacheKey === cacheKeyFor()
      && cachedItems.value.length > 0
      && Date.now() - cacheAt < CACHE_MAX_AGE_MS
  }

  /** Merge preserving object identity, so the open detail doesn't flicker on poll. */
  function mergeItems(incoming: any[]) {
    const existing = new Map(items.value.map((item) => [String(item.id), item]))
    items.value = incoming.map((next) => {
      const prev = existing.get(String(next.id))
      if (!prev) return next
      const changed = ['status', 'lifecycle_state', 'subject', 'body', 'to', 'starred_at']
        .some((key) => prev[key] !== next[key])
      return changed ? next : prev
    })
  }

  async function load({ forPoll = false } = {}) {
    if (!forPoll) loading.value = true
    error.value = ''
    const params = buildParams(filters.value)
    // Set here rather than in buildParams: the counts endpoint shares those
    // params and groups by type, where an ORDER BY means nothing.
    params.set('sort_order', filters.value.sort)
    params.set('limit', String(ACTIONS_PAGE_SIZE))
    params.set('offset', String((filters.value.page - 1) * ACTIONS_PAGE_SIZE))
    // The counts endpoint owns the total, so the list can skip its COUNT(*).
    params.set('include_total', 'false')
    try {
      const data = await apiFetch(`/reachouts?${params.toString()}`)
      const incoming = Array.isArray(data?.reachouts) ? data.reachouts : []
      if (cacheKey === cacheKeyFor()) mergeItems(incoming)
      else items.value = incoming
      cacheKey = cacheKeyFor()
      cacheAt = Date.now()
    } catch (e: any) {
      error.value = e?.message || 'Failed to load actions.'
    } finally {
      loading.value = false
    }
  }

  /**
   * Every row matching the current filters, across all pages — for
   * "select all N matching".
   *
   * Returns rows rather than ids because the caller needs `user_can_act` per
   * row, and pages server-side at the API's 500-row ceiling. Capped: past
   * SELECT_ALL_MAX we stop, and the caller reports how many were actually
   * selected, so a 50k-row filter can't hang the tab or build a request body
   * the API will reject.
   */
  async function fetchAllMatching(): Promise<any[]> {
    const params = buildParams(filters.value)
    params.set('sort_order', filters.value.sort)
    params.set('include_total', 'false')
    const pageSize = 500
    const out: any[] = []
    for (let offset = 0; offset < SELECT_ALL_MAX; offset += pageSize) {
      params.set('limit', String(Math.min(pageSize, SELECT_ALL_MAX - offset)))
      params.set('offset', String(offset))
      const data = await apiFetch(`/reachouts?${params.toString()}`)
      const rows = Array.isArray(data?.reachouts) ? data.reachouts : []
      out.push(...rows)
      if (data?.next_offset === null || rows.length === 0) break
    }
    return out
  }

  /**
   * Card counts. Sends every filter except `type` so each card reports what
   * selecting it would yield — see the endpoint docstring.
   */
  async function loadCounts() {
    const params = buildParams({ ...filters.value, type: '' })
    params.delete('types')
    try {
      const data = await apiFetch(`/reachouts/counts?${params.toString()}`)
      counts.value = (data?.by_type && typeof data.by_type === 'object') ? data.by_type : {}
      total.value = Number(data?.total) || 0
    } catch {
      // Counts are decoration; a failure here must not blank the list.
    }
  }

  async function refresh({ forPoll = false } = {}) {
    await Promise.all([load({ forPoll }), loadCounts()])
  }

  // ── Navigation ─────────────────────────────────────────────────
  // Filter changes reset paging in the SAME push, so a filter change costs one
  // history entry rather than two.


  function setFilters(patch: Partial<ActionsFilters>) {
    const next = { ...filters.value, ...patch }
    if (!('page' in patch)) next.page = 1
    void router.push({
      name: 'actions',
      query: filtersToQuery(next),
    })
  }

  function goToPage(page: number) {
    const clamped = Math.min(Math.max(1, page), pageCount.value)
    if (clamped === filters.value.page) return
    setFilters({ page: clamped })
  }

  /** Detail URL = current filters + the id segment, so back-to-list needs no history. */
  function openDetail(id: string) {
    void router.push({
      name: 'action-detail',
      params: { reachoutId: id },
      query: filtersToQuery(filters.value),
    })
  }

  function backToList() {
    void router.push({
      name: 'actions',
      query: filtersToQuery(filters.value),
    })
  }

  // ── Ordered traversal (j/k) ────────────────────────────────────
  // Crossing a page boundary rather than stopping at it, so paging and keyboard
  // nav behave as one system.

  function indexOf(id: string) {
    return items.value.findIndex((item) => String(item.id) === String(id))
  }

  async function idAfter(id: string): Promise<string | null> {
    const index = indexOf(id)
    if (index === -1) return null
    if (index + 1 < items.value.length) return String(items.value[index + 1].id)
    if (filters.value.page >= pageCount.value) return null
    setFilters({ page: filters.value.page + 1 })
    await load()
    return items.value.length ? String(items.value[0].id) : null
  }

  async function idBefore(id: string): Promise<string | null> {
    const index = indexOf(id)
    if (index === -1) return null
    if (index > 0) return String(items.value[index - 1].id)
    if (filters.value.page <= 1) return null
    setFilters({ page: filters.value.page - 1 })
    await load()
    return items.value.length ? String(items.value[items.value.length - 1].id) : null
  }

  watch(
    () => JSON.stringify(filters.value),
    () => {
      if (cacheIsUsable()) {
        void refresh({ forPoll: true })
        return
      }
      if (cacheKey !== cacheKeyFor()) {
        cachedItems.value = []
        cachedTotal.value = 0
        cachedCounts.value = {}
      }
      void refresh()
    },
    { immediate: true },
  )

  return {
    filters,
    items,
    total,
    counts,
    loading,
    error,
    pageCount,
    rangeStart,
    rangeEnd,
    load,
    loadCounts,
    fetchAllMatching,
    refresh,
    setFilters,
    goToPage,
    openDetail,
    backToList,
    idAfter,
    idBefore,
    cursorId,
  }
}
